import { useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Center,
  CloseButton,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { memo, useCallback, useMemo, useState } from "react";
import { Virtuoso } from "react-virtuoso";
import { UserFragment, UserRoleType } from "../../gql/graphql";
import colors from "../../app/colors";
import {
  escapeRegExp,
  getUserFirstNameLastInitial,
  getUserFullName,
} from "modules/utils/Functions";
import {
  AdminAddUserIcon,
  GroupMembershipIcon,
  ClearSearchIcon,
  CloseIcon,
  SearchIcon,
} from "../icons";
import PhoneOutlineIcon from "../icons/phone-outline-icon";
import VideoCall2Icon from "../icons/video-call-2-icon";
import { useAuth } from "../providers/auth-provider";
import { useUsers } from "../providers/users-provider";
import { useVideoCallProvider } from "../providers/video-call-provider";
import START_CALL_MUTATION from "../providers/video-call-provider/networking/start-call-mutation";
import CircularIconButton from "../_shared/buttons/circular-icon-button";
import UserMention from "../_shared/mentions/user-mention";
import UserAvatar from "../_shared/user-avatar";
import { useOverlay } from "modules/providers/overlay-provider";
import { Logger } from "modules/utils/logger";

const logger = new Logger("synco.CallOptionsModal");

interface IProps {
  userIds: string[];
}

type ParticipantBrowseRow =
  | { kind: "heading"; title: string; sectionTopSpacing?: boolean }
  | { kind: "user"; user: UserFragment };

const CallOptionsModal: React.FC<IProps> = ({ userIds }) => {
  const { usersById, nonEmailActiveUsers } = useUsers();
  const { me } = useAuth();
  const { closeModal, closeDrawer } = useOverlay();
  const starredIdList = useMemo(() => me?.starredIdList ?? [], [me]);

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(userIds);
  const selectedUsers = useMemo(
    () =>
      selectedUserIds
        .map((selectedUserId) => usersById[selectedUserId])
        .filter(
          (user): user is UserFragment =>
            !!user && user.active && user.roleType !== UserRoleType.Email,
        ),
    [selectedUserIds, usersById],
  );

  const callTypes = ["Video Call", "Audio Call"];
  const [selectedCallTypeIndex, setSelectedCallTypeIndex] = useState<number>(0);
  const selectCallType = useCallback(
    (isSelected: boolean, newSelectedCallTypeIndex: number) => {
      if (isSelected) return;
      setSelectedCallTypeIndex(newSelectedCallTypeIndex);
    },
    [],
  );

  const removeParticipant = useCallback((selectedUser: UserFragment) => {
    setSelectedUserIds((prev) => prev.filter((id) => id !== selectedUser.id));
  }, []);

  const [search, setSearch] = useState("");

  const starredUsers = useMemo(
    (): UserFragment[] =>
      starredIdList
        .map((starredId) => usersById[starredId])
        .filter(
          (user): user is UserFragment =>
            !!user && !selectedUserIds.includes(user.id),
        ),
    [selectedUserIds, starredIdList, usersById],
  );

  const allOtherUsers = useMemo(
    (): UserFragment[] =>
      nonEmailActiveUsers.filter(
        (user) =>
          !selectedUserIds.includes(user.id) &&
          !starredIdList.includes(user.id),
      ),
    [nonEmailActiveUsers, selectedUserIds, starredIdList],
  );

  const searchTerms = useMemo(
    () => search.split(/\s/).filter((searchTerm) => searchTerm.length > 0),
    [search],
  );

  const searchUsers = useMemo((): UserFragment[] => {
    if (searchTerms.length === 0) return [];
    return nonEmailActiveUsers.filter(
      (user) =>
        !selectedUserIds.includes(user.id) &&
        searchTerms.some(
          (searchTerm) =>
            user.callout?.match(new RegExp(escapeRegExp(searchTerm), "i")) ||
            getUserFullName(user).match(
              new RegExp(escapeRegExp(searchTerm), "i"),
            ),
        ),
    );
  }, [nonEmailActiveUsers, searchTerms, selectedUserIds]);

  const browseListItems = useMemo((): ParticipantBrowseRow[] => {
    const items: ParticipantBrowseRow[] = [];
    if (starredUsers.length > 0) {
      items.push({ kind: "heading", title: "STARRED CONTACTS" });
      for (const user of starredUsers) {
        items.push({ kind: "user", user });
      }
    }
    if (allOtherUsers.length > 0) {
      items.push({
        kind: "heading",
        title: "ALL USERS",
        sectionTopSpacing: starredUsers.length > 0,
      });
      for (const user of allOtherUsers) {
        items.push({ kind: "user", user });
      }
    }
    return items;
  }, [allOtherUsers, starredUsers]);

  const onSearchChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
    },
    [],
  );

  const onClearSearchClicked = useCallback(() => {
    setSearch("");
  }, []);

  const addParticipant = useCallback(
    (user: UserFragment) => () => {
      setSelectedUserIds((prev) => {
        if (prev.includes(user.id)) return prev;
        return [...prev, user.id];
      });
    },
    [],
  );

  const renderParticipant = useCallback(
    (_: number, user: UserFragment) => (
      <Box
        as="button"
        type="button"
        w="full"
        textAlign="left"
        borderBottom={`1px solid ${colors.gray0}`}
        py="11px"
        cursor="pointer"
        _hover={{ bg: colors.whiteTertiary }}
        onClick={addParticipant(user)}
        aria-label={`Add ${getUserFullName(user)} to call`}
      >
        <HStack justify="space-between">
          <HStack>
            <UserAvatar userId={user.id} size="sm" displayOnlineIndicator />
            <UserMention id={user.id} />
          </HStack>
          <Box p={2} aria-hidden>
            <AdminAddUserIcon boxSize="20px" fill={colors.blue} />
          </Box>
        </HStack>
      </Box>
    ),
    [addParticipant],
  );

  const renderBrowseRow = useCallback(
    (_index: number, item: ParticipantBrowseRow) => {
      if (item.kind === "heading") {
        return (
          <Text
            color={colors.grayMain}
            fontSize={14}
            fontWeight={500}
            py={1}
            borderBottom={`1px solid ${colors.gray0}`}
            mt={item.sectionTopSpacing ? "24px" : undefined}
          >
            {item.title}
          </Text>
        );
      }
      return renderParticipant(0, item.user);
    },
    [renderParticipant],
  );

  const [startCallMutationRequest, { loading }] =
    useMutation(START_CALL_MUTATION);

  const { fullScreenVideoCall } = useVideoCallProvider();

  const startCall = useCallback(() => {
    (async () => {
      const { data, errors } = await startCallMutationRequest({
        variables: {
          invitedUserIdList: selectedUserIds,
          isVideo: selectedCallTypeIndex === 0,
        },
      });

      if (errors) {
        logger.error(errors);
        return;
      }

      if (!data?.startCall) {
        logger.error("Should have data");
        return;
      }

      const c = data.startCall;
      fullScreenVideoCall({ callId: c.id });

      closeDrawer();
      closeModal();
    })();
  }, [
    closeDrawer,
    closeModal,
    fullScreenVideoCall,
    selectedCallTypeIndex,
    selectedUserIds,
    startCallMutationRequest,
  ]);

  return (
    <Modal isOpen onClose={closeModal} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16} p="24px">
        <ModalHeader p={0}>
          <Box position="relative">
            <CloseButton
              height="30px"
              width="30px"
              color={colors.blackSecondary}
              onClick={closeModal}
              position="absolute"
              top={0}
              left={0}
            />
            <Center>
              <Text fontFamily="Outfit" fontSize={20} fontWeight={700}>
                Call Options
              </Text>
            </Center>
          </Box>
        </ModalHeader>
        <ModalBody p={0}>
          <VStack w="full" gap={0} mt="10px">
            <Flex w="full" gap="16px" mb="32px">
              {callTypes.map((callType, i) => {
                const isVideoChat = i === 0;
                const isSelected = i === selectedCallTypeIndex;
                return (
                  <Button
                    key={callType}
                    variant="outline"
                    flex={1}
                    borderColor={isSelected ? colors.blue : colors.grayPrimary}
                    bgColor={isSelected ? colors.blueLight : colors.transparent}
                    _hover={{
                      bgColor: isSelected
                        ? colors.blueLight
                        : colors.transparent,
                      opacity: 0.8,
                    }}
                    _active={{
                      bgColor: isSelected
                        ? colors.blueLight
                        : colors.transparent,
                    }}
                    height="72px"
                    onClick={() => selectCallType(isSelected, i)}
                  >
                    <VStack>
                      {isVideoChat ? (
                        <VideoCall2Icon
                          fill={isSelected ? colors.blue : colors.grayPrimary}
                        />
                      ) : (
                        <PhoneOutlineIcon
                          fill={isSelected ? colors.blue : colors.grayPrimary}
                        />
                      )}
                      <Text
                        fontSize={14}
                        fontWeight={isSelected ? 700 : 500}
                        color={isSelected ? colors.blue : colors.grayMain}
                      >
                        {callType}
                      </Text>
                    </VStack>
                  </Button>
                );
              })}
            </Flex>
            <Box
              w="full"
              overflowX="auto"
              whiteSpace="nowrap"
              mb="10px"
              // Inset so the remove control (absolute, slightly outside the avatar)
              // stays inside the scroll box; overflow-x forces overflow-y to clip.
              pt={3}
              pr={3}
              pb={2}
              pl={1}
            >
              <Flex gap="24px">
                {selectedUsers.map((selectedUser) => (
                  <VStack key={selectedUser.id} position="relative" flexShrink={0}>
                    <UserAvatar
                      userId={selectedUser.id}
                      size="md"
                      displayOnlineIndicator
                    />
                    <Text fontSize={9} fontWeight={600} color={colors.grayMain}>
                      {getUserFirstNameLastInitial(selectedUser)}
                    </Text>
                    <CircularIconButton
                      backgroundColor={colors.white}
                      _hover={colors.white}
                      _active={colors.white}
                      icon={<ClearSearchIcon fill={colors.gray30} />}
                      minW={5}
                      h={5}
                      aria-label="Remove user"
                      onClick={() => removeParticipant(selectedUser)}
                      position="absolute"
                      top={-1}
                      right={-1}
                    />
                  </VStack>
                ))}
              </Flex>
            </Box>
            <HStack
              w="full"
              alignItems="center"
              borderBottom={`1px solid ${colors.black}`}
              pb="4px"
              mb="16px"
            >
              <GroupMembershipIcon
                boxSize="20px"
                fill={colors.blackSecondary}
              />
              <Text
                fontSize={18}
                fontWeight={600}
                color={colors.blackSecondary}
              >
                Add Participants
              </Text>
            </HStack>
            <InputGroup mb="16px">
              <InputLeftElement color={colors.gray30} mx={0}>
                <SearchIcon fill={colors.gray30} boxSize={5} />
              </InputLeftElement>
              <Input
                rounded="full"
                size="md"
                w="full"
                backgroundColor={colors.whiteTertiary}
                placeholder="Search Contacts"
                value={search ?? ""}
                onChange={onSearchChanged}
                color={colors.placeholderText}
              />
              <InputRightElement>
                <Button
                  variant="link"
                  size="sm"
                  leftIcon={<CloseIcon fill={colors.gray30} boxSize={5} />}
                  onClick={onClearSearchClicked}
                />
              </InputRightElement>
            </InputGroup>
            <Box
              w="full"
              h="200px"
              borderBottom={`1px solid ${colors.gray0}`}
            >
              {searchTerms.length > 0 && (
                <Virtuoso
                  data={searchUsers}
                  itemContent={renderParticipant}
                  style={{ height: "100%", width: "100%" }}
                />
              )}
              {searchTerms.length === 0 && browseListItems.length > 0 && (
                <Virtuoso<ParticipantBrowseRow>
                  data={browseListItems}
                  itemContent={renderBrowseRow}
                  style={{ height: "100%", width: "100%" }}
                />
              )}
            </Box>
            <Button
              isDisabled={loading || selectedUserIds.length === 0}
              w="333px"
              mt="24px"
              onClick={startCall}
              fontSize={16}
              fontWeight={500}
            >
              Begin Call
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(CallOptionsModal);
